import { useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

interface FileDropzoneProps {
  /** Called with the picked or dropped files. Never called with an empty list. */
  onFiles: (files: File[]) => void;
  /** Disables click, keyboard and drop. Used while an upload is running. */
  disabled?: boolean;
  /** Allow more than one file per pick/drop. Defaults to true. */
  multiple?: boolean;
  /** `accept` attribute for the hidden input. Defaults to any file. */
  accept?: string;
  /** Text shown in the idle state. */
  label: ReactNode;
  /** Text shown while `disabled` is true (typically upload progress). */
  busyLabel?: ReactNode;
  /** Accessible name for the dropzone button. */
  ariaLabel: string;
  className?: string;
}

/**
 * VIB-2869: delt dropsone for filopplasting. Klikk, tastatur (Enter/Space)
 * og dra-og-slipp gir samme `onFiles`-callback, så kallerne bare trenger å
 * håndtere en liste med filer. Komponenten eier ingen opplastingslogikk.
 */
export default function FileDropzone({
  onFiles,
  disabled = false,
  multiple = true,
  accept,
  label,
  busyLabel,
  ariaLabel,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function emit(list: FileList | null) {
    if (disabled || !list || list.length === 0) return;
    const files = Array.from(list);
    onFiles(multiple ? files : files.slice(0, 1));
  }

  function openPicker() {
    if (!disabled) inputRef.current?.click();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    emit(e.dataTransfer.files);
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      onClick={openPicker}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      data-dragging={isDragging || undefined}
      className={cn(
        "flex items-center gap-2 p-3 border-2 border-dashed rounded-lg transition-colors",
        isDragging ? "border-primary bg-primary/5" : "hover:bg-muted/30",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      )}
    >
      <SrIcon
        name={disabled ? "loader2" : "upload"}
        className={cn("w-4 h-4 text-muted-foreground", disabled && "animate-spin")}
      />
      <span className="text-sm text-muted-foreground">
        {disabled && busyLabel ? busyLabel : label}
      </span>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          emit(e.target.files);
          // Reset so picking the same file twice fires onChange again.
          e.target.value = "";
        }}
      />
    </div>
  );
}
